import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { addMonths } from "date-fns";
import toast from 'react-hot-toast';
import {
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Sort,
    Warning,
    Update,
    KeyboardCommandKey,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setFlights, appendFlights } from "state";
import { 
    LAYOUT_HEIGHT, 
    EXCLUDED_FLIGHT_STATUS 
} from 'state/constants';
import api from 'state/axios';
import { 
    chunkFlights,
    fetchLegMembersByFlt,
} from 'state/api';
import { 
    removeItemByValueImmutable,     
    filterFlights,
    formatTZDate,
} from 'state/helpers';
import { 
    buildPermissionMap, 
    hasRight,
} from 'state/checkPermission';
import { logClientAction } from 'state/logger';
import ScrollToTop from 'components/ScrollToTop';
import NumberControl from 'components/NumberControl';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import ExportButton from 'components/ExportButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import FlightFilterDialog from 'components/FlightFilterDialog';
import CheckResultDialog from 'components/CheckResultDialog';
import CrewListDialog from 'components/CrewListDialog';
import RouteCrewListDialog from 'components/RouteCrewListDialog';
import GenericDialog from 'components/GenericDialog';
import MovementMessageDialog from 'components/MovementMessageDialog';
import { useDelayModel } from "scenes/mlAnalytics/useDelayModel";
import useFlightWorker from './worker';
import FlightList from "./FlightList";
import FlightLogTable from "./FlightLogTable";
import PredictResultDialog from "./PredictResultDialog";
import CommandPalette from "./CommandPalette";
import { useFlightColumns } from "./useFlightColumns";


const FlightSchedule = () => {
    useAuthCheck();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user); 
    const timeMode = useSelector((state) => state.timemode);
    const flightsData = useSelector((state) => state.flights);
    const params = useSelector((state) => state.params);
    const theme = useTheme(); 
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    
    const scrollRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const [offSet, setOffSet] = useState(0);  
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [checksList, setChecksList] = useState([]);
    const [showCheckResultDialog, setShowCheckResultDialog] = useState(false);
    
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [sortOption, setSortOption] = useState('none'); // none, flightReg_fullEtd, fullEtd_flightReg
    
    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [openRouteCrewListDialog, setOpenRouteCrewListDialog] = useState(false);
    const [openMovementMessageDialog, setOpenMovementMessageDialog] = useState(false);
    const [logDialog, setLogDialog] = useState({
        open: false,
        title: "",
        data: [],
        loading: false,
    });

    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);

    const [routeCrewList, setRouteCrewList] = useState([]);
    const [crewRoute, setCrewRoute] = useState({});    
    const [processedFlights, setProcessedFlights] = useState([]);

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const LOAD_CHUNK_DAYS = Number(paramsMap.get("LOAD_CHUNK_DAYS")) || 31;
    const FETCH_TYPE = paramsMap.get("FETCH_TYPE") || "SEQUENCE"; 
    
    const permissionMap = useMemo(
        () => buildPermissionMap(user?.permissions),
        [user?.permissions]
    );
    const canView = hasRight(permissionMap, "FlightSchedule", "view", false);
    const canEdit = hasRight(permissionMap, "FlightSchedule", "update", false);
    const canViewLogs = hasRight(permissionMap, "FlightSchedule", "viewlogs", false);
    const canMovement = hasRight(permissionMap, "FlightSchedule", "movement", false);        
    const canCheck = hasRight(permissionMap, "FlightSchedule", "check", false);
    const canRefresh = hasRight(permissionMap, "FlightSchedule", "refresh", false);

    const { isModelReady, predict } = useDelayModel();
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const [paletteRow, setPaletteRow] = useState(null);
    const [predictResult, setPredictResult] = useState(null);
    const [predictResultOpen, setPredictResultOpen] = useState(false);
    const paletteRowRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                if (paletteRowRef.current) setCommandPaletteOpen(true);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    const handleRowSelect = useCallback((params) => {
        setPaletteRow(params.row);
        paletteRowRef.current = params.row;
    }, []);

    const handlePredict = useCallback((row) => {
        if (!predict || !row) return;
        const d = new Date(row.flightDate);
        const [h, m] = (row.std || "0:0").split(":").map(Number);
        const result = predict({
            dep:       row.dep    || "",
            arr:       row.arr    || "",
            acType:    row.acType || "",
            dayOfWeek: isNaN(d) ? 0 : d.getDay(),
            month:     isNaN(d) ? 0 : d.getMonth(),
            stdHour:   isNaN(h) ? 0 : h,
            stdMinute: isNaN(m) ? 0 : m,
        });
        setPredictResult(result);
        setPredictResultOpen(true);
    }, [predict]);


    // const flightsRef = useRef([]);
    // const [, forceRender] = useState(0);

    // useNDJSONStream({
    //     url: `/flights/stream?from=${startDate}&to=${endDate}`,
    //     batchSize: 300,
    //     onBatch: (batch) => {
    //         flightsRef.current.push(...batch);
    //         // Render má»—i ~1000 records
    //         if (flightsRef.current.length % 1000 === 0) {
    //             forceRender(v => v + 1);
    //         }
    //     },
    //     onDone: () => {
    //         forceRender(v => v + 1);
    //     }
    // });

    useEffect(() => {        
        const processFlights = () => {
            try {      
                setError(null);     
                setLoading(true);
                
                const flights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS);
                setProcessedFlights(flights);
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to process flights!");
            } finally {
                setLoading(false);
            }
        };

        processFlights();        
    }, [flightsData]);
             
    const sortedFlights = useMemo(() => {
        let result = filterFlights({ flights: processedFlights, filters, timeMode });
        
        if (sortOption !== 'none') {
            result = [...result].sort((a, b) => {
                const aEtd = a.fullEtd ? new Date(a.fullEtd) : null;
                const bEtd = b.fullEtd ? new Date(b.fullEtd) : null;
                const aReg = a.flightReg || '';
                const bReg = b.flightReg || '';

                if (aReg !== bReg) return aReg.localeCompare(bReg);
                if (!aEtd && !bEtd) return 0;
                if (!aEtd) return 1;
                if (!bEtd) return -1;
                return aEtd - bEtd;
            });
        }

        return result;
    }, [timeMode, processedFlights, filters, sortOption]);   
    
    const handleCheckErrors = useCallback(() => {
        try {
            const checks = [];

            const addError = (condition, flight, message) => {
                const formattedDate = timeMode === 'UTC' 
                    ? formatTZDate(flight.flightDate, "UTC", "dd/MM/yy") 
                    : formatTZDate(flight.flightDateLocalBase, "UTC", "dd/MM/yy")
                const formattedEtd = timeMode === "UTC" 
                    ? flight.etd 
                    : flight.etdLocalBase;
                const formattedFullEtd = timeMode === "UTC" 
                    ? `${formatTZDate(flight.fullEtd, 'UTC', "HH:mm dd/MM/yy")} (UTC)` 
                    : `${formatTZDate(flight.fullEtdLocalBase, 'UTC', "HH:mm dd/MM/yy")} (LCT)`;

                if (condition) {
                    checks.push({
                        flightNo: flight.flightNo,
                        flightDate: formattedDate,
                        etd: formattedEtd,
                        sector: flight.sector,
                        error: `Flight ${flight.flightNo} ${flight.sector} on ${formattedFullEtd} ${message}`,
                    });
                }
            };

            sortedFlights.forEach((flight) => {
                const {
                    fullAtd,
                    fullTakeOff,
                    fullTouchDown,
                    fullAta,
                } = flight;

                addError(fullTakeOff && fullAtd && fullTakeOff <= fullAtd, flight, "has TakeOff <= ATD");
                addError(fullTouchDown && fullTakeOff && fullTouchDown <= fullTakeOff, flight, "has TouchDown <= TakeOff");
                addError(fullAta && fullTouchDown && fullAta <= fullTouchDown, flight, "has ATA <= TouchDown");
            });

            if (checks.length > 0) {
                setChecksList(checks);
                setShowCheckResultDialog(true);
            } else {
                toast.success("No errors found.");
            }
        } catch (error) {
            toast.error(error?.message || "Failed to check errors!");
        }
    }, [timeMode, sortedFlights]);

    const getMonthLabel = (q) => 
        q < -1 ? "Previous months" :
        q === -1 ? "Previous month" :
        q === 0 ? "Current month" :
        q === 1 ? "Next month" :
        q > 1 ? "Next months" : "";

    const label = getMonthLabel(offSet);

    const handleOffSetChange = (newOffSet) => {
        setOffSet(newOffSet); 
    };

    const handleSortToggle = () => {
        setSortOption((prev) => {
            if (prev === 'none') return 'flightReg_fullEtd';
            if (prev === 'flightReg_fullEtd') return 'none';
            return 'none';
        });
    };

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const handleViewFlight = useCallback(async (param) => {
        try {
            if (!canView) return;

            const { flightDetails, legMembers, jumpSeaters } = await fetchLegMembersByFlt(param.flightDate, param.flightDesc, param.dep);
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
        } 
    }, [canView]);
    
    const handleCloseCrewListDialog = () => {
        setFlight([]);
        setCrewList([]);
        setJumpList([]);
        setOpenCrewListDialog(false);
    }; 

    const handleViewRoute = useCallback((date, routeNo) => {    
        const parseDateToUTC = (dateStr) => {
            const [day, month, year] = dateStr.split('/').map(Number);
            return new Date(Date.UTC(year, month - 1, day));
        };

        const routeDate = parseDateToUTC(date); 
    
        const fetchLegMembersByRoute = async () => {
            try {
                const url = '/legmembers/legmembersbyrouteno';
                const payload = { routeDate, routeNo };                
                const res = await api.post(url, payload);
                                
                if (!res.data) {
                    toast.error("No data found for this route!");
                    return;
                }
                
                const uniqueCrewRoute = res.data.reduce((acc, item) => {
                    const routeKey = `${item.routeDate}-${item.routeNo}`;
                    
                    if (!acc[routeKey]) {
                        acc[routeKey] = {
                            routeDate: item.routeDate,
                            routeNo: item.routeNo,
                            fullEtd: item.fullEtd,
                            fullEtdLocalBase: item.fullEtdLocalBase,
                            routeDetails: [],
                        };
                    }
                    
                    // Check for duplicate based on flightDate, flightNo, and dep
                    const itemKey = `${item.flightDate}-${item.flightNo}-${item.dep}`;
                    if (!acc[routeKey].routeDetails.some(detail => 
                        `${detail.flightDate}-${detail.flightNo}-${detail.dep}` === itemKey
                    )) {
                        acc[routeKey].routeDetails.push(item);
                    }
                    
                    return acc;
                }, {});

                // Sort each route's routeDetails by fullEtd
                Object.values(uniqueCrewRoute).forEach(route => {
                    route.routeDetails.sort((a, b) => new Date(a.fullEtd) - new Date(b.fullEtd));
                });

                setCrewRoute(Object.values(uniqueCrewRoute)[0] || null);

                const uniqueCrewList = Object.values(
                    res.data.reduce((acc, item) => {
                        if (!item.crewCode) return acc;

                        // Only keep the first entry per crewCode
                        if (!acc[item.crewCode]) {
                            acc[item.crewCode] = {
                                crewCode: item.crewCode,
                                crewName: item.crewName,
                                post: item.post,
                                base: item.base,
                                contactNo: item.contactNo,
                                rankPriority: item.rankPriority,
                            };
                        }

                        return acc;
                    }, {})
                ).sort((a, b) => a.rankPriority - b.rankPriority);
                
                setRouteCrewList(uniqueCrewList);

                setOpenRouteCrewListDialog(true);
            } catch (error) {        
                toast.error(error.message || "Failed to fetch leg members!");
            } 
        };
    
        fetchLegMembersByRoute();        
    }, []);      

    const handleCloseRouteCrewListDialog = () => {
        setCrewRoute({})
        setRouteCrewList([]);
        setOpenRouteCrewListDialog(false);
    };
    
    const handleViewLogs = useCallback(async (param) => {   
        try {
            if (!canViewLogs) return;

            const { flightDate, 
                flightDateLocalBase, 
                flightDesc, 
                flightNo, 
                dep, 
                arr 
            } = param;

            const date = timeMode === 'UTC' ? flightDate : flightDateLocalBase;

            setLogDialog({
                open: true,
                title: `Logs: ${flightDesc} ${dep}-${arr} ${formatTZDate(date)}`,
                data: [],
                loading: true,
            });

            const url = '/flightschedulechanges/logsbyflight';
            const payload = { flightDate, flightNo, dep };           
            const res = await api.post(url, payload);
            
            setLogDialog((prev) => ({
                ...prev,
                data: res.data,
                loading: false,
            }));
        } catch (error) {        
            toast.error(error.message || "Failed to fetch flight logs!");
            setLogDialog((prev) => ({ ...prev, loading: false }));
        }    
    }, [timeMode, canViewLogs]); 

    const handleMovementMessage = useCallback(async (_id) => {        
        try {            
            if (!canMovement) return;

            const selectedFlight = processedFlights.find(f => f._id === _id);
            setFlight(selectedFlight);
            setOpenMovementMessageDialog(true);
        } catch (error) {
            setOpenMovementMessageDialog(false);
        } 
    }, [canMovement, processedFlights]);

    const handleCloseMovementMessageDialog = () => {
        setOpenMovementMessageDialog(false);
        setFlight([]);
    }; 

    const handleFlights = useCallback(async () => {
        if (!startDate || !endDate) return null;

        try {
            setError(null);
            setLoading(true);
            setProgress(0);
            
            const prev = Math.min(offSet, 0);
            const next = Math.max(offSet, 0);    
            const fromDate = addMonths(startDate, prev);
            const toDate = addMonths(endDate, next);    

            const controller = new AbortController();
            let isFirstChunk = true;
            await chunkFlights(
                timeMode,
                fromDate,
                toDate,
                LOAD_CHUNK_DAYS,
                FETCH_TYPE,
                {
                    concurrency: 2,
                    retries: 2,
                    backoff: 1000,
                    signal: controller.signal,
                    onProgress: (p) => setProgress(p),
                    onChunk: (chunk) => {
                        if (isFirstChunk) {
                            dispatch(setFlights({ flights: chunk }));
                            isFirstChunk = false;
                        } else {
                            dispatch(appendFlights({ flights: chunk }));
                        }
                    },
                }
            );

            toast.success("Refresh flights successfully.");
        } catch (error) {
            setError(error);
            toast.error(error?.message || "Failed to load flights!");
        } finally {
            setLoading(false);
        }
    }, [
        dispatch,
        timeMode, 
        startDate, 
        endDate, 
        offSet,
        LOAD_CHUNK_DAYS,
        FETCH_TYPE,
    ]);

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(processedFlights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(processedFlights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [processedFlights]);   
    
    const { columns, actionColumn } = useFlightColumns({
        timeMode,
        isMobile,
        params,
        handleViewRoute,
        canView,
        canEdit,
        navigate,
        handleViewFlight,
    });


    
    const hiddenColumns = useMemo(() => {
        return {
            _id: false,
            no: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightActualDayArr: false,
            flightActualDayArrLocalBase: false,
            flightDesc: false,
            flightCarrier: false,
            acType: false,
            flightReg: false,
            flightType: false,        
            dep: false,        
            arr: false,        
            // std: false,
            // stdLocalBase: false,
            // sta: false,
            // staLocalBase: false,
            etd: false,
            etdLocalBase: false,
            eta: false,
            etaLocalBase: false,
            doorClose: false,
            doorCloseLocalBase: false,
            atd: false,
            atdLocalBase: false,
            ata: false,
            ataLocalBase: false,
            takeOff: false,
            takeOffLocalBase: false,
            touchDown: false,
            touchDownLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            scheduleTat: false,
            estimatedTat: false,
            tat: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            flightDepDelays: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            remarks: false,
            sectorType: false,
            flightStatus: false,
            expectedPaxPerCategory: false,
            paxPerCategory: false,
            expectedNoOfPax: false,
            actualNoOfPax: false,
            noOfPax: false,
            capacity: false,
            loadFactor: false,
            crewRoutes: false,
            routeCount: false,
            isGroundTimeRecovery: false,
            isBlockTimeRecovery: false,
            depGate: false,
            depStand: false,
            arrGate: false,
            arrStand: false,
        }
    }, []);

    const nonMobileHiddenColumns = useMemo(() => {
        return {
            _id: false,
            flightDate: false,
            flightDateLocalBase: false,
            flightActualDayArr: false,
            flightActualDayArrLocalBase: false,
            flightDesc: false,
            flightCarrier: false,
            flightType: false,
            dep: false,        
            arr: false,    
            // std: false,
            // stdLocalBase: false,
            // sta: false,
            // staLocalBase: false,
            // etd: false,
            // etdLocalBase: false,
            // eta: false,
            // etaLocalBase: false,
            doorClose: false,
            doorCloseLocalBase: false,
            taxiOut: false,
            taxiIn: false,
            scheduleTat: false,
            estimatedTat: false,
            tat: false,
            tatDiff: false,
            airMinutes: false,
            blockMinutes: false,
            flightDepDelays: false,
            depDelaysTime: false,
            flightArrDelays: false,
            arrDelaysTime: false,
            sectorType: false,
            flightStatus: false,
            expectedPaxPerCategory: false,
            paxPerCategory: false,
            expectedNoOfPax: false,
            actualNoOfPax: false,
            noOfPax: false,
            capacity: false,
            loadFactor: false,
            crewRoutes: false,
            routeCount: false,
            isGroundTimeRecovery: false,
            isBlockTimeRecovery: false,
            depGate: false,
            depStand: false,
            arrGate: false,
            arrStand: false,
        }
    }, []);
        
    const columnsToShow = [...columns, ...actionColumn];
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        isMobile ? hiddenColumns : nonMobileHiddenColumns
    );

    useEffect(() => {
        setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
    }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);
    
    useFlightWorker(handleFlights, !loading);


    return (
        <Box className="main-container-box">                 
            <FlexBetween>
                <Header 
                    title="Flight Schedule" 
                    subtitle={loading ? "Loading..." : `List of ${sortedFlights?.length || 0} Flights`}
                />
                {!loading && (                    
                    <Box display="flex" alignItems="center" gap={1}>  
                        {!isMobile && canRefresh && (     
                            <Box display="flex" alignItems="center" gap={1}>  
                                <Tooltip title={label} arrow>
                                    <span>
                                        <NumberControl
                                            min={-23}
                                            max={23}
                                            value={offSet}
                                            onChange={handleOffSetChange} 
                                            textFieldWidth={45}
                                        />
                                    </span>
                                </Tooltip>
                                <Tooltip title="Refresh" arrow>
                                    <span>
                                        <StyledButton onClick={handleFlights}>
                                            <Update />
                                        </StyledButton>
                                    </span>              
                                </Tooltip>
                            </Box>
                        )}  

                        {canCheck && (                    
                            <Tooltip title="Check errors" arrow>
                                <span>
                                    <StyledButton 
                                        onClick={() => {
                                            if (!canCheck) return;
                                            handleCheckErrors();
                                        }}
                                    >
                                        <Warning />
                                    </StyledButton>
                                </span>                            
                            </Tooltip>
                        )}

                        <ExportButton
                            data={sortedFlights}
                            fileName="FlightList"
                            tooltip="Export"
                            onExport={(format) => logClientAction("EXPORT_REPORT", { fileName: "FlightList", format, startDate, endDate })}
                        />  
                        
                        {!isMobile && (
                            <Tooltip title="Command Palette (Ctrl+K)" arrow>
                                <span>
                                    <StyledButton
                                        disabled={!paletteRow}
                                        onClick={() => setCommandPaletteOpen(true)}
                                    >
                                        <KeyboardCommandKey />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        )}

                        <Tooltip title={`Sort by ${sortOption === 'flightReg_fullEtd' ? 'Reg + Etd' : 'Etd'}`} arrow>
                            <span>
                                <StyledButton 
                                    onClick={handleSortToggle}
                                    sx={{                                        
                                        color: sortOption === 'none' 
                                        ? `${theme.palette.text.primary} !important` 
                                        : `${theme.palette.primary.dark} !important`,
                                    }}
                                >
                                    <Sort />
                                </StyledButton>                                
                            </span>                            
                        </Tooltip>
                        
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>          
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        alignContent="center" 
                        gap={1}
                    >
                        <CircularProgress />
                        <Typography>{progress || 0}%</Typography>
                    </Box>
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (      
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >          
                    <Box
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
                    >               
                        {isMobile ? (
                            <FlightList 
                                flights={sortedFlights}                                
                                onClick={handleViewFlight}
                            />
                        ) : (
                            <DataGridComponent
                                rows={sortedFlights}
                                columns={columnsToShow}
                                loading={loading}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                props={{
                                    onRowClick: handleRowSelect,
                                    getRowClassName: (params) =>
                                        params.id === paletteRow?._id ? "Mui-selected" : "",
                                    sx: { cursor: "pointer" },
                                }}
                            />
                        )}
                        
                        <ScrollToTop scrollRef={scrollRef} />
                    </Box>
                </Box>
            )}
            
            {showCheckResultDialog && (
                <CheckResultDialog
                    open={showCheckResultDialog}
                    onClose={() => setShowCheckResultDialog(false)}
                    title="Check"
                    checksList={checksList}
                    descriptionKey="error"
                />          
            )}
                
            {isFilterDialogOpen && (
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            )}
            
            {openCrewListDialog && (
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            )}

            {openRouteCrewListDialog && (
                <RouteCrewListDialog 
                    open={openRouteCrewListDialog}
                    onClose={handleCloseRouteCrewListDialog}
                    crewRoute={crewRoute}
                    crewList={routeCrewList}
                />
            )}
            
            {logDialog.open && (
                <GenericDialog
                    open={logDialog.open}
                    onClose={() => setLogDialog({ ...logDialog, open: false })}
                    title={logDialog.title}
                    loading={logDialog.loading}
                >
                    <FlightLogTable data={logDialog.data} />
                </GenericDialog>
            )}

            {openMovementMessageDialog && (
                <MovementMessageDialog
                    open={openMovementMessageDialog}
                    onClose={handleCloseMovementMessageDialog}
                    flightRegOptions={flightRegOptions}
                    flight={flight}
                />
            )}

            <CommandPalette
                open={commandPaletteOpen}
                onClose={() => setCommandPaletteOpen(false)}
                row={paletteRow}
                onViewLogs={handleViewLogs}
                onMovement={handleMovementMessage}
                onPredict={handlePredict}
                canViewLogs={canViewLogs}
                canMovement={canMovement}
                isModelReady={isModelReady}
            />

            <PredictResultDialog
                open={predictResultOpen}
                onClose={() => setPredictResultOpen(false)}
                flight={paletteRow}
                result={predictResult}
            />
        </Box>
    );
};

export default FlightSchedule;
